"use client";

import posthog from "posthog-js";

import { useCallback, useMemo, useState } from "react";

import type { AIChatContextValue } from "@/components/ai-chat/contexts/ai-chat-context";
import type { ChatMessage, FeedbackType } from "@/components/ai-chat/types";

import type { ChatHistoryEntry } from "@/types/chat-thread";
import { AIFeedbackType } from "@/types/enums";

import { requestData } from "@/api/api-config";

interface UseChatFeedbackOptions {
  messages: ChatMessage[];
  setMessages?: (updater: (prev: ChatMessage[]) => ChatMessage[]) => void;
  patchFeedbackEntry?: (
    qaId: string,
    updater: (entry: ChatHistoryEntry) => ChatHistoryEntry,
    scopeId?: string,
  ) => void;
  feedbackScopeId?: string;
  submitFeedback?: (
    qaId: string,
    feedbackType: FeedbackType | null,
    text: string | null,
    scopeId?: string,
  ) => Promise<unknown>;
  /** Included in posthog analytics events. Omit to skip tracking. */
  chatType?: string;
}

export function useChatFeedback({
  messages,
  setMessages,
  patchFeedbackEntry,
  feedbackScopeId,
  submitFeedback: submitFeedbackMutation,
  chatType,
}: UseChatFeedbackOptions) {
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [pendingFeedback, setPendingFeedback] = useState<{
    qaId: string;
    feedbackType: FeedbackType;
    index: number;
    scopeId?: string;
  } | null>(null);

  const submitFeedback = useCallback(
    async (
      qaId: string,
      feedbackType: FeedbackType,
      index: number,
      text: string | null,
      scopeId?: string,
    ) => {
      // Optimistic UI update
      if (patchFeedbackEntry) {
        patchFeedbackEntry(
          qaId,
          (entry) => ({
            ...entry,
            feedbackType,
            feedbackText: text,
            pendingFeedback: true,
          }),
          scopeId,
        );
      } else {
        setMessages?.((prev) =>
          prev.map((entry, i) =>
            i === index
              ? { ...entry, feedbackType, pendingFeedback: true }
              : entry,
          ),
        );
      }

      try {
        if (submitFeedbackMutation) {
          await submitFeedbackMutation(qaId, feedbackType, text, scopeId);
        } else {
          await requestData(`ai/chat-history/${qaId}/feedback`, "post", {
            json: { feedback_type: feedbackType, feedback_text: text },
          });
        }
        if (patchFeedbackEntry) {
          patchFeedbackEntry(
            qaId,
            (entry) => {
              const { pendingFeedback: _pendingFeedback, ...rest } = entry;
              return rest;
            },
            scopeId,
          );
        } else {
          setMessages?.((prev) =>
            prev.map((entry, i) =>
              i === index ? { ...entry, pendingFeedback: false } : entry,
            ),
          );
        }
      } catch {
        // Revert on error
        if (patchFeedbackEntry) {
          patchFeedbackEntry(
            qaId,
            (entry) => {
              const { pendingFeedback: _pendingFeedback, ...rest } = entry;
              return { ...rest, feedbackType: null };
            },
            scopeId,
          );
        } else {
          setMessages?.((prev) =>
            prev.map((entry, i) =>
              i === index
                ? { ...entry, feedbackType: null, pendingFeedback: false }
                : entry,
            ),
          );
        }
      }
    },
    [patchFeedbackEntry, setMessages, submitFeedbackMutation],
  );

  const handleFeedbackClick = useCallback(
    (qaId: string | undefined, feedbackType: FeedbackType, index: number) => {
      const currentMessage = messages[index];
      const isToggleOff = currentMessage?.feedbackType === feedbackType;
      const scopeId = feedbackScopeId;
      if (chatType) {
        posthog.capture("patient_summary_qa_feedback_clicked", {
          chat_type: chatType,
          feedback_type:
            feedbackType === AIFeedbackType.POSITIVE ? "positive" : "negative",
          is_toggle_off: isToggleOff,
        });
      }

      // Toggle off if clicking same type
      if (isToggleOff) {
        if (qaId && patchFeedbackEntry) {
          patchFeedbackEntry(
            qaId,
            (entry) => ({
              ...entry,
              feedbackType: null,
              feedbackText: null,
              pendingFeedback: true,
            }),
            scopeId,
          );
        } else {
          setMessages?.((prev) =>
            prev.map((entry, i) =>
              i === index
                ? { ...entry, feedbackType: null, pendingFeedback: true }
                : entry,
            ),
          );
        }

        if (qaId) {
          (submitFeedbackMutation
            ? submitFeedbackMutation(qaId, null, null, scopeId)
            : requestData(`ai/chat-history/${qaId}/feedback`, "post", {
                json: { feedback_type: null, feedback_text: null },
              })
          )
            .then(() => {
              if (patchFeedbackEntry) {
                patchFeedbackEntry(
                  qaId,
                  (entry) => {
                    const { pendingFeedback: _pendingFeedback, ...rest } =
                      entry;
                    return { ...rest, feedbackType: null, feedbackText: null };
                  },
                  scopeId,
                );
              } else {
                setMessages?.((prev) =>
                  prev.map((entry, i) =>
                    i === index
                      ? { ...entry, pendingFeedback: false, feedbackType: null }
                      : entry,
                  ),
                );
              }
            })
            .catch(() => {
              if (patchFeedbackEntry) {
                patchFeedbackEntry(
                  qaId,
                  (entry) => {
                    const { pendingFeedback: _pendingFeedback, ...rest } =
                      entry;
                    return { ...rest, feedbackType };
                  },
                  scopeId,
                );
              } else {
                setMessages?.((prev) =>
                  prev.map((entry, i) =>
                    i === index
                      ? { ...entry, feedbackType, pendingFeedback: false }
                      : entry,
                  ),
                );
              }
            });
        } else {
          setMessages?.((prev) =>
            prev.map((entry, i) =>
              i === index ? { ...entry, pendingFeedback: false } : entry,
            ),
          );
        }
        return;
      }

      // Negative feedback → open dialog
      if (feedbackType === AIFeedbackType.NEGATIVE) {
        setPendingFeedback({
          qaId: qaId || "",
          feedbackType,
          index,
          scopeId,
        });
        setShowFeedbackDialog(true);
      } else {
        // Positive feedback → submit immediately
        if (qaId) {
          submitFeedback(qaId, feedbackType, index, null, scopeId);
        }
      }
    },
    [
      messages,
      patchFeedbackEntry,
      setMessages,
      submitFeedback,
      submitFeedbackMutation,
      feedbackScopeId,
      chatType,
    ],
  );

  const handleFeedbackDialogSubmit = useCallback(() => {
    if (!pendingFeedback) return;
    if (pendingFeedback.qaId) {
      submitFeedback(
        pendingFeedback.qaId,
        pendingFeedback.feedbackType,
        pendingFeedback.index,
        feedbackText.trim() || null,
        pendingFeedback.scopeId,
      );
    }
    setShowFeedbackDialog(false);
    setFeedbackText("");
    setPendingFeedback(null);
  }, [pendingFeedback, feedbackText, submitFeedback]);

  return {
    handleFeedbackClick,
    showFeedbackDialog,
    setShowFeedbackDialog,
    feedbackText,
    setFeedbackText,
    handleFeedbackDialogSubmit,
  };
}

/**
 * Wraps `useChatFeedback` and returns a context-shaped feedback value
 * suitable for passing to `AIChat.Provider`'s `feedback` prop.
 */
export function useChatFeedbackContext(
  options: UseChatFeedbackOptions,
): NonNullable<AIChatContextValue["feedback"]> {
  const fb = useChatFeedback(options);

  return useMemo(
    () => ({
      state: {
        isOpen: fb.showFeedbackDialog,
        feedbackText: fb.feedbackText,
      },
      actions: {
        openFeedbackDialog: () => fb.setShowFeedbackDialog(true),
        closeFeedbackDialog: () => {
          fb.setShowFeedbackDialog(false);
          fb.setFeedbackText("");
        },
        setFeedbackText: fb.setFeedbackText,
        submitFeedback: fb.handleFeedbackDialogSubmit,
      },
    }),
    [
      fb.showFeedbackDialog,
      fb.feedbackText,
      fb.setShowFeedbackDialog,
      fb.setFeedbackText,
      fb.handleFeedbackDialogSubmit,
    ],
  );
}
